// DeliverableDetailDialog.js
// Extracted from AIWorkforcePage.js for better maintainability

import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { ScrollArea } from '../../../components/ui/scroll-area';
import { 
  FileText, Image as ImageIcon, TrendingUp, Mail, Target, Download, Copy, 
  Edit, Trash2, Link, Shuffle, History, FileSpreadsheet, FileType, UserPlus 
} from 'lucide-react';
import { format } from 'date-fns';
import ReactMarkdown from 'react-markdown';
import { toast } from 'sonner';

/**
 * Dialog for viewing deliverable details with export, edit, and regeneration options
 * 
 * @param {Object} props
 * @param {boolean} props.open - Controls dialog visibility
 * @param {function} props.onOpenChange - Callback when dialog open state changes
 * @param {Object} props.deliverable - The selected deliverable to display
 * @param {boolean} props.editing - Whether edit mode is active
 * @param {function} props.onEditingChange - Callback when editing state changes
 * @param {Object} props.editForm - Current edit form data
 * @param {function} props.onEditFormChange - Callback when edit form changes
 * @param {function} props.onSaveEdit - Callback when save edit is clicked
 * @param {function} props.onDelete - Callback when delete is clicked
 * @param {function} props.onExportExcel - Callback when export to Excel is clicked
 * @param {function} props.onExportWord - Callback when export to Word is clicked
 * @param {function} props.onImportToProspects - Callback when import to prospects is clicked
 * @param {function} props.onLinkToCampaign - Callback when link to campaign is clicked
 * @param {function} props.onRegenerate - Callback when regenerate is clicked
 * @param {function} props.onViewHistory - Callback when view history is clicked
 * @param {boolean} props.exportLoading - Whether export is in progress
 */
export const DeliverableDetailDialog = ({
  open,
  onOpenChange,
  deliverable,
  editing,
  onEditingChange,
  editForm,
  onEditFormChange,
  onSaveEdit,
  onDelete,
  onExportExcel,
  onExportWord,
  onImportToProspects,
  onLinkToCampaign,
  onRegenerate,
  onViewHistory,
  exportLoading = false
}) => {
  const getTypeIcon = () => {
    switch (deliverable?.type) {
      case 'image': return <ImageIcon className="h-5 w-5 text-pink-500" />;
      case 'document': return <FileText className="h-5 w-5 text-blue-500" />;
      case 'analysis': return <TrendingUp className="h-5 w-5 text-purple-500" />;
      case 'email': return <Mail className="h-5 w-5 text-amber-500" />;
      default: return <FileText className="h-5 w-5 text-slate-500" />;
    }
  };

  const handleDownloadImage = () => {
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${deliverable.base64}`;
    link.download = `deliverable_${deliverable.id}.png`;
    link.click();
    toast.success('Image downloaded');
  };

  const handleCopyContent = () => {
    navigator.clipboard.writeText(deliverable.content);
    toast.success('Content copied to clipboard');
  };

  const handleStartEdit = () => {
    onEditFormChange({
      title: deliverable?.title || deliverable?.task_title || '',
      description: deliverable?.description || '',
      type: deliverable?.type || ''
    });
    onEditingChange(true);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {getTypeIcon()}
            Deliverable Details
          </DialogTitle>
          <DialogDescription>
            Generated by {deliverable?.agent_name} • {deliverable?.type} • {deliverable?.format}
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto space-y-4">
          {/* Task Context */}
          <div className="bg-slate-50 rounded-lg p-4 border">
            <h4 className="font-medium mb-2 flex items-center gap-2">
              <Target className="h-4 w-4" />
              Task: {deliverable?.task_title}
            </h4>
            {deliverable?.original_context && (
              <p className="text-sm text-muted-foreground">{deliverable.original_context}</p>
            )}
          </div>

          {/* Content Display */}
          {deliverable?.type === 'image' && deliverable?.base64 ? (
            <div className="border rounded-lg overflow-hidden bg-slate-100">
              <img 
                src={`data:image/png;base64,${deliverable.base64}`} 
                alt="Generated" 
                className="w-full h-auto max-h-[500px] object-contain"
              />
              {deliverable.prompt && (
                <div className="p-3 bg-slate-50 border-t">
                  <p className="text-xs text-muted-foreground"><strong>Prompt:</strong> {deliverable.prompt}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="border rounded-lg p-4 bg-white">
              <ScrollArea className="h-[400px]">
                <div className="prose prose-sm max-w-none">
                  <ReactMarkdown>
                    {deliverable?.content || deliverable?.preview || 'No content available'}
                  </ReactMarkdown>
                </div>
              </ScrollArea>
            </div>
          )}

          {/* Metadata */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="bg-slate-50 p-3 rounded-lg">
              <p className="text-muted-foreground">Created</p>
              <p className="font-medium">
                {deliverable?.created_at 
                  ? format(new Date(deliverable.created_at), 'PPpp')
                  : 'Unknown'}
              </p>
            </div>
            <div className="bg-slate-50 p-3 rounded-lg">
              <p className="text-muted-foreground">Task ID</p>
              <p className="font-mono text-xs">{deliverable?.task_id}</p>
            </div>
          </div>

          {/* AI Reasoning Section - Shows the "logic" behind the deliverable */}
          {deliverable?.ai_reasoning && (
            <div className="mt-4 border rounded-lg overflow-hidden">
              <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-3">
                <h4 className="font-semibold flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                  AI Reasoning Process
                </h4>
              </div>
              <div className="p-3 space-y-3 bg-slate-50">
                {deliverable.ai_reasoning.left_brain && (
                  <div className="p-2 bg-blue-50 rounded border-l-4 border-blue-500">
                    <p className="text-xs font-semibold text-blue-700 mb-1">🧠 Left Brain (Analytics)</p>
                    <p className="text-xs text-slate-600">{deliverable.ai_reasoning.left_brain.assessment?.slice(0, 200)}...</p>
                  </div>
                )}
                {deliverable.ai_reasoning.right_brain && (
                  <div className="p-2 bg-pink-50 rounded border-l-4 border-pink-500">
                    <p className="text-xs font-semibold text-pink-700 mb-1">🎨 Right Brain (Creative)</p>
                    <p className="text-xs text-slate-600">{deliverable.ai_reasoning.right_brain.assessment?.slice(0, 200)}...</p>
                  </div>
                )}
                {deliverable.ai_reasoning.cortex && (
                  <div className="p-2 bg-purple-50 rounded border-l-4 border-purple-500">
                    <p className="text-xs font-semibold text-purple-700 mb-1">🔮 Cortex (Synthesis)</p>
                    <p className="text-xs text-slate-600">{deliverable.ai_reasoning.cortex.assessment?.slice(0, 200)}...</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        
        <DialogFooter className="border-t pt-4 flex-wrap gap-2">
          <div className="flex gap-2 flex-1 flex-wrap">
            {/* Export Buttons */}
            {deliverable?.content && (
              <>
                <Button variant="outline" size="sm" disabled={exportLoading} onClick={onExportExcel} data-testid="deliverable-export-excel">
                  <FileSpreadsheet className="h-4 w-4 mr-1" />
                  Excel
                </Button>
                <Button variant="outline" size="sm" disabled={exportLoading} onClick={onExportWord} data-testid="deliverable-export-word">
                  <FileType className="h-4 w-4 mr-1" />
                  Word
                </Button>
              </>
            )}
            
            {/* Import to Prospects Button */}
            {deliverable?.content?.includes('|') && (
              <Button variant="outline" size="sm" onClick={onImportToProspects} data-testid="deliverable-import">
                <UserPlus className="h-4 w-4 mr-1" />
                Import to Prospects
              </Button>
            )}
            
            {/* Link to Campaign Button */}
            <Button variant="outline" size="sm" onClick={onLinkToCampaign} data-testid="deliverable-link-campaign">
              <Link className="h-4 w-4 mr-1" />
              Link to Campaign
            </Button>
            
            {/* Edit Button */}
            <Button variant="outline" size="sm" onClick={handleStartEdit} data-testid="deliverable-edit">
              <Edit className="h-4 w-4 mr-1" />
              Edit
            </Button>
            
            {/* Regenerate / Get Options Button */}
            <Button
              variant="outline"
              size="sm"
              className="bg-amber-50 border-amber-200 hover:bg-amber-100"
              onClick={onRegenerate}
              data-testid="deliverable-regenerate"
            >
              <Shuffle className="h-4 w-4 mr-1" />
              {deliverable?.type === 'image' ? 'Generate Options' : 'Create Variations'}
            </Button>
            
            {/* View History Button */}
            <Button variant="outline" size="sm" onClick={onViewHistory} data-testid="deliverable-history">
              <History className="h-4 w-4 mr-1" />
              History
            </Button>
          </div>
          
          <div className="flex gap-2">
            {/* Delete Button */}
            <Button variant="destructive" size="sm" onClick={() => onDelete(deliverable?.id)} data-testid="deliverable-delete">
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="deliverable-close">
              Close
            </Button>
            {deliverable?.type === 'image' && deliverable?.base64 && (
              <Button onClick={handleDownloadImage} data-testid="deliverable-download">
                <Download className="h-4 w-4 mr-2" />
                Download Image
              </Button>
            )}
            {deliverable?.content && (
              <Button onClick={handleCopyContent} data-testid="deliverable-copy">
                <Copy className="h-4 w-4 mr-2" />
                Copy Content
              </Button>
            )}
          </div>
        </DialogFooter>
        
        {/* Edit Mode Overlay */}
        {editing && (
          <div className="absolute inset-0 bg-white/95 z-50 p-6 overflow-y-auto">
            <div className="max-w-lg mx-auto space-y-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Edit className="h-5 w-5" />
                Edit Deliverable
              </h3>
              <div>
                <Label>Title</Label>
                <Input
                  value={editForm.title || ''}
                  onChange={(e) => onEditFormChange({ ...editForm, title: e.target.value })}
                  placeholder="Deliverable title"
                  data-testid="deliverable-edit-title"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={editForm.description || ''}
                  onChange={(e) => onEditFormChange({ ...editForm, description: e.target.value })}
                  placeholder="Description"
                  rows={3}
                  data-testid="deliverable-edit-description"
                />
              </div>
              <div>
                <Label>Type</Label>
                <Select 
                  value={editForm.type || ''} 
                  onValueChange={(v) => onEditFormChange({ ...editForm, type: v })}
                >
                  <SelectTrigger data-testid="deliverable-edit-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="document">Document</SelectItem>
                    <SelectItem value="image">Image</SelectItem>
                    <SelectItem value="analysis">Analysis</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="task_output">Task Output</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2 justify-end pt-4">
                <Button variant="outline" onClick={() => onEditingChange(false)} data-testid="deliverable-edit-cancel">
                  Cancel
                </Button>
                <Button onClick={onSaveEdit} data-testid="deliverable-edit-save">
                  Save Changes
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DeliverableDetailDialog;
